import { EmptyStateAlt } from '@ovComponents/2-component/emptyState/emptyState';
import { Box, Skeleton, Stack } from 'hephaestus/web';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { buildHouseholdModelPortfolioIpsFields } from '../householdModelPortfolio.ips.utils';
import type {
  HouseholdModelPortfolioAction,
  HouseholdModelPortfolioModel,
  HouseholdModelPortfolioWidgetOptions,
} from '../householdModelPortfolio.types';
import { isHouseholdModelPortfolioDetailsMissing } from '../householdModelPortfolio.utils';
import { HouseholdModelPortfolioIpsField } from './householdModelPortfolioIpsField';

type HouseholdModelPortfolioIpsProps = {
  action: HouseholdModelPortfolioAction;
  options: HouseholdModelPortfolioWidgetOptions;
  model: HouseholdModelPortfolioModel | null;
  modelDetailsLoading: boolean;
  modelDetailsError?: Error;
};

const IPS_SKELETON_KEYS = [
  'description',
  'investment-objective',
  'disclaimer',
] as const;

const HouseholdModelPortfolioIpsSkeleton = () => (
  <Stack spacing={3} data-testid='household-model-portfolio-ips-loading'>
    {IPS_SKELETON_KEYS.map((skeletonKey) => (
      <Box key={skeletonKey}>
        <Skeleton
          variant='rectangular'
          width='25%'
          height={16}
          sx={{ mb: 1 }}
        />
        <Skeleton variant='rectangular' width='80%' height={20} />
      </Box>
    ))}
  </Stack>
);

export const HouseholdModelPortfolioIps = ({
  action,
  options,
  model,
  modelDetailsLoading,
  modelDetailsError,
}: HouseholdModelPortfolioIpsProps) => {
  const { t } = useTranslation(['components', 'modelPortfolios', 'shared']);
  const hasAttachedModel = action === 'edit';
  const detailsMissing = isHouseholdModelPortfolioDetailsMissing({
    action,
    model,
    modelDetailsLoading,
    modelDetailsError,
  });

  const fields = useMemo(
    () => buildHouseholdModelPortfolioIpsFields(model, options, t),
    [model, options, t],
  );

  return (
    <Box data-testid='household-model-portfolio-ips-section'>
      {modelDetailsLoading ? (
        <HouseholdModelPortfolioIpsSkeleton />
      ) : modelDetailsError || detailsMissing ? (
        <Box data-testid='household-model-portfolio-ips-error'>
          <EmptyStateAlt
            sx={{ height: '112px' }}
            title={t('components:householdModelPortfolio.ips.error')}
            subtitle={t('shared:errorStateSubText')}
          />
        </Box>
      ) : !hasAttachedModel ? (
        <Box data-testid='household-model-portfolio-ips-no-model-attached'>
          <EmptyStateAlt
            sx={{ height: '112px' }}
            title={t(
              'components:householdModelPortfolio.modelDetails.noModelAttached',
            )}
          />
        </Box>
      ) : (
        <Stack spacing={3}>
          {fields.map((field) => (
            <HouseholdModelPortfolioIpsField key={field.id} field={field} />
          ))}
        </Stack>
      )}
    </Box>
  );
};
